import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail, fileUrl, downloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Eye, Download, Plus, Pencil, Trash2 } from "lucide-react";

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [docForm, setDocForm] = useState({ title: "", file: null });
  const [gmail, setGmail] = useState("");
  const [savingGmail, setSavingGmail] = useState(false);

  const load = async () => {
    const me = await api.get("/employees/me");
    setProfile(me.data);
    setGmail(me.data.personal_gmail || "");
    const [docs, ledgerRes] = await Promise.all([
      api.get(`/employees/${me.data.id}/documents`),
      api.get(`/employees/${me.data.id}/ledger`),
    ]);
    setDocuments(docs.data);
    setLedger(ledgerRes.data);
  };

  useEffect(() => {
    load();
  }, []);

  const saveGmail = async () => {
    setSavingGmail(true);
    try {
      await api.put("/employees/me", { personal_gmail: gmail || null });
      toast.success("Gmail address saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingGmail(false);
    }
  };

  const uploadProfileFile = async (fileType, file) => {
    const fd = new FormData();
    fd.append("file_type", fileType);
    fd.append("file", file);
    try {
      await api.post(`/employees/${profile.id}/profile-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteProfileFile = async (fileType) => {
    try {
      await api.delete(`/employees/${profile.id}/profile-file/${fileType}`);
      toast.success("File removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitDocument = async () => {
    if (!docForm.title || !docForm.file) {
      toast.error("Title and file are required");
      return;
    }
    const fd = new FormData();
    fd.append("doc_type", "signed_agreement");
    fd.append("title", docForm.title);
    fd.append("file", docForm.file);
    try {
      await api.post(`/employees/${profile.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Agreement uploaded");
      setDocForm({ title: "", file: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const replaceDocument = async (docId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/employees/${profile.id}/documents/${docId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document file replaced");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await api.delete(`/employees/${profile.id}/documents/${docId}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!profile) return null;

  return (
    <DashboardLayout title="My Profile">
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6 flex items-center justify-between" data-testid="my-profile-header">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#1C1C1E]">{profile.name}</h2>
          <p className="text-sm text-[#4B5563]">{profile.designation}</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="overview" data-testid="profile-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" data-testid="profile-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="profile-tab-ledger">Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Email" value={profile.email} />
            <Field label="Date of Joining" value={profile.date_of_joining} />
            <Field label="Reporting Manager" value={profile.reporting_manager} />
            <Field label="Phone" value={profile.phone} />
            <Field label="Address" value={profile.address} className="col-span-2" />
            <Field label="Scope of Work" value={profile.sow} className="col-span-2" />
          </div>
          <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563]">Notification Email</h4>
            <div className="flex items-center gap-3">
              <Input
                data-testid="my-gmail-input"
                type="email"
                placeholder="you@gmail.com"
                value={gmail}
                onChange={(e) => setGmail(e.target.value)}
                className="max-w-sm"
              />
              <button
                onClick={saveGmail}
                disabled={savingGmail}
                data-testid="my-gmail-save-button"
                className="text-sm bg-[#00823B] hover:bg-[#006A2F] text-white rounded-md px-3 py-1.5 disabled:opacity-60"
              >
                {savingGmail ? "Saving..." : "Save"}
              </button>
            </div>
            <p className="text-xs text-[#4B5563]">Optional — add a personal Gmail address to also receive task assignment emails there, in addition to {profile.email}.</p>
          </div>
          <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563]">Bank & Compensation Details</h4>
            <p className="text-xs text-[#4B5563] -mt-1">Set by HR/Admin — contact your admin to update these.</p>
            <div className="grid grid-cols-2 gap-4 text-sm" data-testid="my-bank-details">
              <Field label="Account Holder Name" value={profile.account_holder_name} />
              <Field label="Bank Account Number" value={profile.bank_account_number} />
              <Field label="IFSC Code" value={profile.ifsc_code} />
              <Field label="Stipend Amount" value={profile.stipend_amount != null ? `\u20B9${Number(profile.stipend_amount).toLocaleString("en-IN")}` : null} />
            </div>
          </div>
          <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563]">My HR Files</h4>
            <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
              <span className="text-sm">Resume</span>
              <div className="flex items-center gap-3">
                {profile.resume_path && (
                  <>
                    <a data-testid="my-resume-view-link" href={fileUrl(profile.resume_path)} target="_blank" rel="noreferrer" className="text-xs text-[#00823B] hover:underline flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" /> View
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="my-resume-delete-button" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Resume?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteProfileFile("resume")} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
                <label className="text-xs text-[#4B5563] cursor-pointer hover:underline" data-testid="my-resume-upload-label">
                  {profile.resume_path ? "Replace" : "Upload"}
                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadProfileFile("resume", e.target.files[0])} />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
              <span className="text-sm">ID Document</span>
              <div className="flex items-center gap-3">
                {profile.id_document_path && (
                  <>
                    <a data-testid="my-iddoc-view-link" href={fileUrl(profile.id_document_path)} target="_blank" rel="noreferrer" className="text-xs text-[#00823B] hover:underline flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" /> View
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="my-iddoc-delete-button" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove ID Document?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteProfileFile("id_document")} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
                <label className="text-xs text-[#4B5563] cursor-pointer hover:underline" data-testid="my-iddoc-upload-label">
                  {profile.id_document_path ? "Replace" : "Upload"}
                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadProfileFile("id_document", e.target.files[0])} />
                </label>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-5 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
          <div className="border border-[#E2E8F0] rounded-md p-3 space-y-2">
            <p className="text-xs text-[#4B5563] uppercase tracking-wide">Upload Signed Agreement</p>
            <Input data-testid="my-doc-title-input" placeholder="Document title" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} />
            <Input data-testid="my-doc-file-input" type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} />
            <button onClick={submitDocument} data-testid="my-doc-submit-button" className="text-sm bg-[#00823B] hover:bg-[#006A2F] text-white rounded-md px-3 py-1.5 flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Upload
            </button>
          </div>
          <div className="space-y-2">
            {documents.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No documents yet.</p>}
            {documents.map((d) => (
              <div key={d.id} data-testid={`my-document-row-${d.id}`} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
                <div>
                  <p className="text-sm font-medium text-[#1C1C1E]">{d.title}</p>
                  <p className="text-xs text-[#4B5563] capitalize">{d.doc_type.replace(/_/g, " ")}</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer" title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                  <a href={downloadUrl(d.file_path, d.title)} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                  {d.doc_type === "signed_agreement" && (
                    <>
                      <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`my-document-replace-${d.id}`} title="Replace file">
                        <Pencil className="h-4 w-4" />
                        <input type="file" className="hidden" onChange={(e) => e.target.files[0] && replaceDocument(d.id, e.target.files[0])} />
                      </label>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`my-document-delete-${d.id}`} className="text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{d.title}"?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteDocument(d.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-5 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                <th className="px-5 py-3 font-medium">Month</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-[#4B5563]">No ledger entries yet.</td></tr>
              )}
              {ledger.map((l) => (
                <tr key={l.id} data-testid={`my-ledger-row-${l.id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                  <td className="px-5 py-3">{l.month}</td>
                  <td className="px-5 py-3 capitalize">{l.type}</td>
                  <td className="px-5 py-3 font-medium">₹{Number(l.amount).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3"><StatusBadge value={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Field({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-[#1C1C1E] font-medium">{value || "—"}</p>
    </div>
  );
}
